<template>
  <div class="q-pa-md">
    <q-spinner
        v-if="isLoading"
        style="margin: auto"
        height="300px"
        width="300px"
        color="primary"
    />
    <div v-else class="row justify-center">
      <div>
        <q-date
            v-model="uiDate"
            :events="eventDays"
            :options="dateOptionsFn"
            event-color="primary"
            dark
            @navigation="onDateNavigation"
        />
      </div>

      <div class="col-md-6 col-sm-9 col-xs-11 q-mx-lg">
        <div class="row justify-center">
          <q-btn icon="chevron_left" flat size="24px" @click="selectPrevious" />
          <q-btn icon="chevron_right" flat size="24px" @click="selectNext" />
        </div>

        <q-tab-panels dark v-model="uiDate" animated>
          <q-tab-panel
              v-for="ev in eventDays"
              :key="ev"
              :name="ev"
          >
            <div class="text-h4 q-mb-md">
              {{ formatPanelHeader(ev) }}
            </div>

            <q-list separator dark>
              <q-item
                  v-for="appointment in getAppointmentsOf(ev)"
                  :key="appointment.id"
                  clickable
                  v-ripple
                  :disable="isDisabled(appointment)"
                  @click="onSelect(appointment)"
              >
                <q-item-section side>
                  <q-checkbox
                      :model-value="selectedAppointment?.id === appointment.id"
                      @click.stop="onSelect(appointment)"
                  />
                </q-item-section>

                <q-item-section class="items-center">
                  {{ formatTime(appointment.start) }}
                </q-item-section>

                <q-item-section>
                  <div class="row">
                    <div class="col-12 flex flex-center">
                      {{ sanitizeSummary(appointment.summary) }}
                    </div>
                  </div>
                </q-item-section>
              </q-item>
            </q-list>
          </q-tab-panel>
        </q-tab-panels>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, defineProps, defineEmits, onMounted } from 'vue'
import axios from 'axios'
import { DateTime } from 'luxon'
import { Appointment } from "@/models/appointment";


const props = defineProps<{
  /** v-model: komplettes Appointment rein/raus */
  modelValue?: Appointment | null
  /** Optional: eigener Endpoint zum Laden */
  fetchUrl?: string
}>()

/* eslint-disable */
const emit = defineEmits<{
  /** v-model */
  (e: 'update:modelValue', value: Appointment | null): void
  /** ausgewähltes Appointment als Objekt (Komfort-Event) */
  (e: 'update:selectedAppointment', value: Appointment | null): void
  /** alle geladenen freien Termine */
  (e: 'update:appointments', value: Appointment[]): void
}>()

const isLoading = ref(false)
const appointments = ref<Appointment[]>([])
const selectedAppointment = ref<Appointment | null>(null)

/** q-date erwartet 'YYYY/MM/DD' */
const uiDate = ref<string | null>(null)

/** ---- Lifecycle ---- */
onMounted(load)

/** ---- Laden ---- */
async function load() {
  isLoading.value = true
  try {
    const url = props.fetchUrl ?? 'https://getavailableslots-3uz45qef3a-uc.a.run.app'
    const { data } = await axios.get(url)

    appointments.value = (data as any[]).map((item) => ({
      id: item.id,
      summary: normalizeSummary(item.summary),
      start: new Date(item.start),
      end: new Date(item.end),
    }))

    emit('update:appointments', appointments.value)

    // Voreinstellung: entweder v-model vorwählen oder den ersten freien Termin
    preselectFromModelOrFirst()
  } catch (err) {
    console.error('Fehler beim Laden der Termine:', err)
    appointments.value = []
    emit('update:appointments', appointments.value)
    selectedAppointment.value = null
    emit('update:selectedAppointment', null)
    emit('update:modelValue', null)
  } finally {
    isLoading.value = false
  }
}

/** ---- v-model beobachten: wenn parent ein Appointment setzt, hier vorwählen ---- */
watch(
    () => props.modelValue,
    (appt) => {
      if (!appointments.value.length) return
      if (!appt) {
        selectedAppointment.value = null
        emit('update:selectedAppointment', null)
        return
      }
      // Falls das Objekt nicht exakt aus unserer Liste stammt, per ID „vereinheitlichen“
      const match = appointments.value.find(a => a.id === appt.id) ?? appt
      selectedAppointment.value = match
      uiDate.value = toUIDate(match.start)
      emit('update:selectedAppointment', match)
    },
    { immediate: true }
)

/** ---- Hilfen ---- */
function toUIDate(d: Date) {
  const dt = DateTime.fromJSDate(d)
  return `${dt.year}/${String(dt.month).padStart(2, '0')}/${String(dt.day).padStart(2, '0')}`
}

function formatTime(d: Date) {
  return DateTime.fromJSDate(d).toLocaleString(DateTime.TIME_24_SIMPLE)
}

function formatPanelHeader(uid: string) {
  const [y, m, day] = uid.split('/').map(Number)
  const dt = DateTime.fromObject({ year: y, month: m, day })
  return dt.toLocaleString({ day: '2-digit', month: 'short' })
}

function normalizeSummary(s: string) {
  return isAvailableSummary(s) ? 'Frei' : s
}

function sanitizeSummary(s: string) {
  return s
}

function isAvailableSummary(s: string) {
  const normalized = s?.trim?.().toLowerCase?.() ?? ''
  return normalized === 'frei' || normalized === 'freix' || normalized === 'mal'
}

function isDisabled(a: Appointment) {
  return !isAvailableSummary(a.summary)
}

/** Alle Tage, an denen es Termine gibt (YYYY/MM/DD) */
const eventDays = computed<string[]>(() => {
  const set = new Set(appointments.value.map(a => toUIDate(a.start)))
  return Array.from(set).sort()
})

/** q-date options */
function dateOptionsFn(day: string) {
  return eventDays.value.includes(day)
}

/** Termine eines Tages */
function getAppointmentsOf(uid: string): Appointment[] {
  return appointments.value
      .filter(a => toUIDate(a.start) === uid)
      .sort((a, b) => a.start.getTime() - b.start.getTime())
}

/** Navigation im Kalender */
function onDateNavigation(payload: { year: number; month: number }) {
  return payload
}

/** Pfeil-Tasten unter der Liste */
function selectPrevious() {
  if (!uiDate.value) return
  const idx = eventDays.value.indexOf(uiDate.value)
  if (idx > 0) uiDate.value = eventDays.value[idx - 1]
}

function selectNext() {
  if (!uiDate.value) return
  const idx = eventDays.value.indexOf(uiDate.value)
  if (idx >= 0 && idx < eventDays.value.length - 1) {
    uiDate.value = eventDays.value[idx + 1]
  }
}

/** Auswahl-Handler */
function onSelect(a: Appointment) {
  if (isDisabled(a)) return
  if (selectedAppointment.value?.id === a.id) return

  selectedAppointment.value = a
  uiDate.value = toUIDate(a.start)

  // v-model out: jetzt das vollständige Appointment-Objekt
  emit('update:modelValue', a)
  emit('update:selectedAppointment', a)
}

/** Voreinstellung beim Laden */
function preselectFromModelOrFirst() {
  if (props.modelValue) {
    const match = appointments.value.find(a => a.id === props.modelValue!.id) ?? props.modelValue
    selectedAppointment.value = match
    uiDate.value = toUIDate(match.start)
    emit('update:selectedAppointment', match)
    emit('update:modelValue', match)
    return
  }
  // sonst: ersten freien Termin wählen
  const firstFree = appointments.value.find(a => !isDisabled(a)) || null
  selectedAppointment.value = firstFree
  uiDate.value = firstFree ? toUIDate(firstFree.start) : (eventDays.value[0] ?? null)
  emit('update:selectedAppointment', firstFree ?? null)
  emit('update:modelValue', firstFree ?? null)
}
</script>
